// src/pages/admin/AdminTeacherBooks.tsx
import React, { useEffect, useState } from 'react';
import { collection, doc, getDocs, updateDoc, deleteDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref as sRef, getDownloadURL } from 'firebase/storage';
import { db, storage, uploadFileWithToken } from '../../lib/firebase';
import { useThemePalette } from '../../hooks/useThemePalette';
import { useThemeStore } from '../../stores/useThemeStore';

interface TeacherBook {
  id:          string;
  title:       string;
  description: string;
  subject:     string;
  pdfUrl:      string;
}

const EMPTY: Omit<TeacherBook, 'id'> = { title: '', description: '', subject: '', pdfUrl: '' };

const inputStyle = (C: ReturnType<typeof useThemePalette>, isDark: boolean): React.CSSProperties => ({
  padding: '9px 12px', borderRadius: '10px', width: '100%', boxSizing: 'border-box',
  border: `1px solid ${C.border}`,
  background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
  color: C.text, fontSize: '0.85rem', fontFamily: 'inherit', outline: 'none',
});

export default function AdminTeacherBooks() {
  const C      = useThemePalette();
  const isDark = useThemeStore(s => s.theme) === 'dark';

  const [items,       setItems]       = useState<TeacherBook[]>([]);
  const [editing,     setEditing]     = useState<TeacherBook | null>(null);
  const [form,        setForm]        = useState<Omit<TeacherBook, 'id'>>(EMPTY);
  const [pdfFile,     setPdfFile]     = useState<File | null>(null);
  const [showForm,    setShowForm]    = useState(false);
  const [status,      setStatus]      = useState('');
  const [isErr,       setIsErr]       = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [delConfirm,  setDelConfirm]  = useState<string | null>(null);

  const load = () => {
    getDocs(collection(db, 'teacherBooks'))
      .then(snap => {
        const list: TeacherBook[] = [];
        snap.forEach(d => list.push({ id: d.id, ...(d.data() as Omit<TeacherBook, 'id'>) }));
        setItems(list.sort((a, b) => a.title.localeCompare(b.title)));
      })
      .catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setPdfFile(null);
    setStatus('');
    setShowForm(true);
  };

  const openEdit = (item: TeacherBook) => {
    setEditing(item);
    setForm({ title: item.title, description: item.description, subject: item.subject, pdfUrl: item.pdfUrl });
    setPdfFile(null);
    setStatus('');
    setShowForm(true);
  };

  const save = async () => {
    if (!form.title.trim()) { setStatus('Title required'); setIsErr(true); return; }
    setSaving(true); setStatus('Saving...'); setIsErr(false);
    try {
      let pdfUrl = form.pdfUrl;

      if (pdfFile) {
        const id = editing?.id ?? `tb_${Date.now()}`;
        const path = `teacherBooks/${id}/${Date.now()}_${pdfFile.name}`;
        const storageRef = sRef(storage, path);
        await uploadFileWithToken(storageRef, pdfFile);
        pdfUrl = await getDownloadURL(storageRef);
      }

      const data = { title: form.title, description: form.description, subject: form.subject, pdfUrl };

      if (editing) {
        await updateDoc(doc(db, 'teacherBooks', editing.id), data);
      } else {
        await addDoc(collection(db, 'teacherBooks'), { ...data, createdAt: serverTimestamp() });
      }

      setStatus('Saved!'); setIsErr(false);
      setTimeout(() => { setStatus(''); setShowForm(false); load(); }, 1200);
    } catch (e: unknown) {
      setStatus(e instanceof Error ? e.message : 'Error'); setIsErr(true);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'teacherBooks', id));
      setItems(prev => prev.filter(i => i.id !== id));
      setDelConfirm(null);
    } catch {
      setStatus('Delete failed'); setIsErr(true);
    }
  };

  return (
    <div style={{ padding: '32px', maxWidth: '860px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <div style={{ fontSize: '1.1rem', fontWeight: 800, color: C.text, marginBottom: '4px' }}>Teacher's Books</div>
          <div style={{ fontSize: '0.8rem', color: C.muted }}>Manage PDF teacher resources</div>
        </div>
        <button
          onClick={openAdd}
          style={{ padding: '9px 20px', borderRadius: '999px', border: 'none', background: C.accent, color: '#fff', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          + Add Book
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div style={{ background: C.card, border: `1px solid ${C.borderH}`, borderRadius: '16px', padding: '24px', marginBottom: '28px' }}>
          <div style={{ fontSize: '0.88rem', fontWeight: 700, color: C.text, marginBottom: '16px' }}>
            {editing ? 'Edit Teacher Book' : 'New Teacher Book'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: C.muted, display: 'block', marginBottom: '6px' }}>Title *</label>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={inputStyle(C, isDark)} />
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: C.muted, display: 'block', marginBottom: '6px' }}>Subject</label>
              <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="e.g. Sports English" style={inputStyle(C, isDark)} />
            </div>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: C.muted, display: 'block', marginBottom: '6px' }}>Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} style={{ ...inputStyle(C, isDark), resize: 'vertical' }} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: C.muted, display: 'block', marginBottom: '6px' }}>
              PDF File {form.pdfUrl && <span style={{ color: C.success }}>(current file exists)</span>}
            </label>
            <input type="file" accept="application/pdf,.pdf" onChange={e => setPdfFile(e.target.files?.[0] ?? null)} style={{ fontSize: '0.82rem', color: C.text }} />
            {pdfFile && <div style={{ fontSize: '0.75rem', color: C.premium, marginTop: '6px' }}>{pdfFile.name}</div>}
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button onClick={() => void save()} disabled={saving} style={{ padding: '8px 20px', borderRadius: '999px', border: 'none', background: C.accent, color: '#fff', fontSize: '0.78rem', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => setShowForm(false)} style={{ padding: '8px 16px', borderRadius: '999px', border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              Cancel
            </button>
            {status && <span style={{ fontSize: '0.78rem', color: isErr ? C.error : C.success }}>{status}</span>}
          </div>
        </div>
      )}

      {/* List */}
      {items.length === 0 ? (
        <div style={{ color: C.muted, fontSize: '0.85rem' }}>No teacher books yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {items.map(item => (
            <div key={item.id} style={{
              background: C.card, border: `1px solid ${C.border}`,
              borderRadius: '14px', padding: '16px 20px',
              display: 'flex', alignItems: 'flex-start', gap: '16px',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: C.text }}>{item.title}</div>
                {item.subject && <div style={{ fontSize: '0.75rem', color: C.accent, marginTop: '2px' }}>{item.subject}</div>}
                {item.description && <div style={{ fontSize: '0.78rem', color: C.muted, marginTop: '6px', lineHeight: 1.5 }}>{item.description}</div>}
                {item.pdfUrl && (
                  <a href={item.pdfUrl} target="_blank" rel="noreferrer" style={{ fontSize: '0.72rem', color: C.accent, textDecoration: 'none', marginTop: '6px', display: 'inline-block' }}>
                    View PDF →
                  </a>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                <button onClick={() => openEdit(item)} style={{ padding: '6px 14px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Edit
                </button>
                {delConfirm === item.id ? (
                  <button onClick={() => void remove(item.id)} style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid rgba(248,113,113,0.3)', background: 'rgba(248,113,113,0.1)', color: C.error, fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Sure?
                  </button>
                ) : (
                  <button onClick={() => setDelConfirm(item.id)} style={{ padding: '6px 14px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
